import React, { useState } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { financeOperatorAPI } from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const FinanceOperatorRegistration = ({ isOpen, onClose, editingOperator = null, onSuccess }) => {
  const { isDarkMode } = useAdminTheme();
  const [formData, setFormData] = useState({
    firstName: editingOperator?.firstName || '',
    lastName: editingOperator?.lastName || '',
    email: editingOperator?.email || '',
    phoneNumber: editingOperator?.phoneNumber || '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.phoneNumber && !/^[+]?[\d\s\-()]{10,15}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!editingOperator && !formData.password) {
      errors.password = 'Password is required';
    } else if (!editingOperator && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    try {
      setIsCreating(true);
      
      if (editingOperator) {
        const response = await financeOperatorAPI.updateFinanceOperator(editingOperator._id, formData);
        if (response.success) {
          toast.success('Finance operator updated successfully');
          onSuccess();
          onClose();
        } else {
          toast.error(response.message || 'Failed to update finance operator');
        }
      } else {
        const response = await financeOperatorAPI.createFinanceOperator(formData);
        if (response.success) {
          toast.success('Finance operator created successfully');
          onSuccess();
          onClose();
        } else {
          toast.error(response.message || 'Failed to create finance operator');
        }
      }
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: ''
      });
      setFormErrors({});
      setShowPassword(false);
      
    } catch (error) {
      console.error('Error saving finance operator:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.Fxdata?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        toast.error('Failed to save finance operator');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: ''
    });
    setFormErrors({});
    setShowPassword(false);
    onClose();
  };

  return (
    <div className={`fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center`}>
      <div className={`relative w-full max-w-md my-8 ${isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'} rounded-lg shadow-lg transition-colors duration-300`}>
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className={`text-xl font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            {editingOperator ? 'Edit Finance Operator' : 'Register Finance Operator'}
          </h3>
          <button
            onClick={handleClose}
            className={`transition-colors duration-300 ${
              isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                  formErrors.firstName 
                    ? 'border-red-300' 
                    : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                }`}
                required
              />
              {formErrors.firstName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                  formErrors.lastName 
                    ? 'border-red-300' 
                    : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                }`}
                required
              />
              {formErrors.lastName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                  formErrors.email 
                    ? 'border-red-300' 
                    : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                }`}
                required
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                  formErrors.phoneNumber 
                    ? 'border-red-300' 
                    : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                }`}
              />
              {formErrors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{formErrors.phoneNumber}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Password {editingOperator ? '(leave blank to keep current)' : '*'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                    formErrors.password 
                      ? 'border-red-300' 
                      : (isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900')
                  }`}
                  required={!editingOperator}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className={`h-5 w-5 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                    }`} />
                  ) : (
                    <Eye className={`h-5 w-5 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                    }`} />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className={`btn ${isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Saving...' : (editingOperator ? 'Update Finance Operator' : 'Create Finance Operator')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinanceOperatorRegistration;
